/**
 * Camera QR scanning: native `BarcodeDetector` when the browser has it (Chrome/Android, Safari 17+ on some
 * builds), otherwise the zxing-wasm reader, loaded lazily with its wasm bundled locally (works offline).
 */

/** Minimal typing for the Shape Detection API, which is not in TypeScript's DOM lib. */
interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
interface BarcodeDetectorConstructor {
  new (options: { formats: string[] }): BarcodeDetectorLike;
  getSupportedFormats(): Promise<string[]>;
}

type FrameDecoder = (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => Promise<string | null>;

const SCAN_INTERVAL_MS = 200;

async function nativeDecoder(): Promise<FrameDecoder | null> {
  const ctor = (globalThis as { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
  if (!ctor) return null;
  try {
    const formats = await ctor.getSupportedFormats();
    if (!formats.includes("qr_code")) return null;
  } catch {
    return null;
  }
  const detector = new ctor({ formats: ["qr_code"] });
  return async (canvas) => {
    const codes = await detector.detect(canvas);
    return codes[0]?.rawValue ?? null;
  };
}

async function zxingDecoder(): Promise<FrameDecoder> {
  const [{ prepareZXingModule, readBarcodes }, { default: wasmUrl }] = await Promise.all([
    import("zxing-wasm/reader"),
    import("zxing-wasm/reader/zxing_reader.wasm?url"),
  ]);
  prepareZXingModule({
    overrides: { locateFile: (path: string, prefix: string) => (path.endsWith(".wasm") ? wasmUrl : prefix + path) },
    fireImmediately: true,
  });
  return async (canvas, ctx) => {
    const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const results = await readBarcodes(image, { formats: ["QRCode"], maxNumberOfSymbols: 1 });
    return results.find((r) => r.isValid)?.text ?? null;
  };
}

export class CameraUnavailableError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = "CameraUnavailableError";
  }
}

/** A running scan session; call `stop()` to release the camera. */
export interface ScanSession {
  stop(): void;
}

/**
 * Starts the rear camera into `video` and calls `onCode` once with the first decoded QR text.
 * Throws `CameraUnavailableError` when there is no camera or permission was denied.
 */
export async function startQrScan(video: HTMLVideoElement, onCode: (text: string) => void): Promise<ScanSession> {
  if (!navigator.mediaDevices?.getUserMedia) throw new CameraUnavailableError("getUserMedia unsupported");
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
  } catch (error) {
    throw new CameraUnavailableError(error);
  }

  let stopped = false;
  let timer: number | undefined;
  const stop = () => {
    stopped = true;
    if (timer !== undefined) window.clearTimeout(timer);
    for (const track of stream.getTracks()) track.stop();
    video.srcObject = null;
  };

  try {
    video.srcObject = stream;
    video.setAttribute("playsinline", "true");
    video.muted = true;
    await video.play();
    const decode = (await nativeDecoder()) ?? (await zxingDecoder());
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("2D canvas unavailable");

    const tick = async () => {
      if (stopped) return;
      if (video.videoWidth > 0) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);
        try {
          const text = await decode(canvas, ctx);
          if (text && !stopped) {
            stop();
            onCode(text);
            return;
          }
        } catch (error) {
          console.warn("QR decode failed", error);
        }
      }
      if (!stopped) timer = window.setTimeout(() => void tick(), SCAN_INTERVAL_MS);
    };
    void tick();
  } catch (error) {
    stop();
    throw new CameraUnavailableError(error);
  }
  return { stop };
}
